import { useState, type ReactNode } from 'react';
import { readCollapsed, writeCollapsed } from '../../../lib/portalNavRail';
import { signOut } from 'firebase/auth';
import { firebaseAuth } from '../../../lib/firebase';
import { portalThemeStyle } from '../../../lib/portalReport/themeVars';
import { modeToggleLabel, REPORT_MODE_ATTR } from '../../../lib/portalReport/reportTheme';
import type { PortalBrandTheme } from '../../../types/portalReport';
import { usePortalBrand } from './PortalBrandContext';
import { PortalLockup } from './PortalLockup';
import { usePortalMode } from './PortalModeContext';
import type { PortalNavSelection } from '../../../lib/portalReport/locationSelection';
import { PortalNav } from './PortalNav';

// CLIENT PORTAL — the shell. Rendered OUTSIDE SignedInGuard and AppShell, so a
// client never sees the agency sidebar, the account switcher, or any of the
// internal nav (the public preview / survey / hosted-report pages are the
// precedent for rendering outside the staff shell).
//
// ⚠️⚠️ IT TAKES THE REPORT'S LOOK (2026-09-02) RATHER THAN KEEPING ITS OWN. The
// shell was generic white/blue with a green that was not `kelly`, sitting beside
// a report with a full token system — two looks in one product, which is the
// thing to avoid. The root carries `.kyck-portal`, which is where every
// `--rpt-*` token is defined, so "adopt the scope" was already half-done; what
// changed is that the chrome now READS those tokens (see `.psh-*` in
// src/index.css) instead of shadcn's slate defaults.
//
// ⚠️⚠️ AND IT OWNS THE MODE, for the whole portal. The report used to own its own
// light/dark state, which was fine while the shell had no look to be out of step
// with — the moment the shell adopted the report's tokens, a client switching the
// report to light would have got a light report inside a dark shell, and the
// choice would have appeared to be forgotten on the way back to the home page.
// One control, one state: `PortalModeProvider` holds it, the shell renders the
// only toggle, and the report reads it.
//
// ⚠️ NO VOLT ANYWHERE IN THE CHROME. Brand rule 4 allows exactly one volt element
// per page, and the report already spends it on the hero figure — so the shell's
// active-nav marker is `kelly`, with `ink` on it rather than white (6.18:1
// against 2.84:1, rule 3).
//
// ⚠️⚠️ PHASE 2 THEMING IS ACCENTS ON KYCK CHROME, NOT A SKIN. The brand supplies
// the lockup; the surfaces, ink, spacing and layout stay ours. "Client portal"
// stays in the header beside the brand for exactly that reason — the client is
// looking at their brand's report, produced by us, and a full skin would obscure
// whose tool it is the moment a group owner opens reports for two marques.

type Props = {
  children: ReactNode;
  /** Shown top-right when there is a session to end. */
  signedInAs?: string | null;
  /**
   * Does the CHROME carry the lockup? (§7, 2026-09-04)
   *
   * ⚠️⚠️ THE ONE-LOCKUP RULE, APPLIED IN THE OTHER DIRECTION. The signed-out page
   * makes the lockup the PAGE rather than the chrome — Scott asked for a big Kyck
   * logo and "Client portal" on it — and rendering both would put two on one
   * screen, which is exactly how this portal came to have two looks.
   *
   * ⚠️ The same shape as `RestyledReport`'s `hosted`: whoever owns the lockup on
   * a given page says so, and the other side renders none.
   */
  chromeLockup?: boolean;
  /**
   * ⚠️ OPT-IN, and off by default. The nav is rendered only on pages that
   * already have a session: the sign-in and finish pages have nothing to
   * navigate, and a nav there would offer a client sections they cannot open.
   *
   * ⚠️ The nav resolves its OWN contents from `/api/portal/me` — the FULL scope,
   * which does not move — rather than taking them as a prop. A report's
   * `accounts` is post-narrowing, so a nav fed from it would collapse to one row
   * the moment a client drilled into a club and leave no way back.
   */
  nav?: boolean;
  /**
   * ⚠️ The REPORT supplies its own full-bleed bands and its own inner max-width,
   * so it opts out of the shell's reading column. Everything else — the home
   * page, the error states — gets the padded column.
   */
  bleed?: boolean;
  /**
   * ⚠️ Round 5 — FILL MODE, for the library document viewer and nothing else.
   * The page is bounded to the viewport (`height: 100dvh`) and the main column
   * becomes a flex column, so the document's frame takes the height left below
   * the header and there is ONE scrollbar: the document's. ⚠️ It adds NO
   * `overflow` anywhere — stickyNav.test.ts asserts no rail ancestor declares
   * one — and it keeps a minimum frame height, so on a short phone the page can
   * still scroll rather than being all frame.
   */
  fill?: boolean;
  /**
   * ⚠️ WIDE MODE (Run J). The padded column's semantics — normal document scroll,
   * vertical padding — with a much larger cap, for a page whose content is a
   * working surface rather than prose (the KyckSocial composer). NOT `fill`:
   * bounding a scrolling composer to the viewport is wrong. The 1320px default is
   * left alone; it is right for the home page and the error states, and a page
   * DECLARES what it needs.
   */
  wide?: boolean;
  /**
   * ⚠️ OPTIONAL OVERRIDE ONLY. The brand normally comes from the CONTEXT, which
   * every portal page is inside — threading it through eight call sites would be
   * eight chances to forget it, and an unbranded error page inside a branded
   * portal is exactly the kind of miss that survives review.
   */
  theme?: PortalBrandTheme | null;
  /** Override for the lockup's text fallback. Normally from the context. */
  brandName?: string | null;
  /**
   * What the report on this page is narrowed to, so the nav can underline the
   * matching club row (§B, 2026-09-03).
   *
   * ⚠️ THREADED, NOT RE-DERIVED. Only the report knows what the server actually
   * narrowed to, and the nav must not hold a second opinion about it — the club
   * name in the heading and the underline in the nav are one fact.
   *
   * ⚠️ OPTIONAL because the nav renders on pages that have no report at all
   * (the home page). Absent = no club row is active, which is correct there.
   */
  navSelection?: PortalNavSelection | undefined;
  /**
   * The page fills the whole content width while the nav is folded (the Leads centre: a list beside a detail). ⚠️ Opt-in per page, never
   * every `wide` page: the others keep their own max-widths.
   */
  fullWidthWhenFolded?: boolean;
};

export function PortalShell({
  children,
  signedInAs,
  chromeLockup = true,
  nav = false,
  bleed = false,
  fill = false,
  wide = false,
  theme: themeProp,
  brandName: brandNameProp,
  navSelection,
  fullWidthWhenFolded = false,
}: Props) {
  const ctx = usePortalBrand();
  const { mode, toggle } = usePortalMode();
  const theme = themeProp !== undefined ? themeProp : ctx.theme;
  const brandName = brandNameProp !== undefined ? brandNameProp : ctx.brandName;
  // ⚠️ READ SYNCHRONOUSLY IN THE FIRST RENDER (a lazy initialiser), never in an effect: an effect would paint the wide rail and then fold it,
  // a visible jump on every page load. Storage errors read as expanded (portalNavRail.ts). The stylesheet only honours it at ≥1024px.
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(next);
      return next;
    });
  };
  const railFolded = nav && collapsed;

  return (
    <div
      className={`kyck-portal psh-root${fill ? ' psh-root--fill' : ''}${railFolded ? ' psh-root--collapsed' : ''}`}
      // ⚠️ THE MODE ATTRIBUTE GOES ON THE ELEMENT CARRYING `.kyck-portal`, never
      // on :root and never on html — kyck internal must be untouched by it.
      {...({ [REPORT_MODE_ATTR]: mode } as Record<string, string>)}
      style={portalThemeStyle(theme)}
    >
      <header className="psh-top">
        {/* ⚠️ THE ONE LOCKUP, shared with the report's own header. Two
            implementations of "show the brand" is how this portal came to have
            two looks; see PortalLockup.tsx. */}
        {/* ⚠️ "Client portal" is grouped WITH the lockup, not left as a
            sibling of it: `.psh-top` is `justify-content: space-between`, so a
            loose third child would be pushed to the middle of the header. */}
        {/* ⚠️ An empty div when the PAGE owns the lockup (§7): `.psh-top` is
            `justify-content: space-between`, so removing this child entirely would
            pull the controls to the left rather than leaving them where they are. */}
        <div className="psh-lockup">
          {chromeLockup && (
            <>
              <PortalLockup
                brandName={brandName}
                brandLogoUrl={theme?.logoUrl}
                brandLogoAlt={theme?.logoAlt}
              />
              <span className="rpt-muted text-sm">Client portal</span>
            </>
          )}
        </div>
        <div className="psh-controls">
          {/* ⚠️ THE ONLY MODE CONTROL IN THE PORTAL. The label names the mode it
              will SWITCH TO, which is what a reader expects of a toggle. */}
          <button type="button" aria-pressed={mode === 'light'} onClick={toggle}>
            {modeToggleLabel(mode)}
          </button>
          {signedInAs && (
            <>
              <span className="rpt-muted hidden text-sm sm:inline">{signedInAs}</span>
              <button type="button" onClick={() => void signOut(firebaseAuth)}>
                Sign out
              </button>
            </>
          )}
        </div>
      </header>

      <div className="psh-body">
        {nav && <PortalNav selection={navSelection} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />}
        <main
          className={
            fill
              ? 'psh-main psh-main--fill'
              : bleed
                ? 'psh-main'
                : wide
                  ? `psh-main psh-main--padded psh-main--wide${fullWidthWhenFolded ? ' psh-main--fold-full' : ''}`
                  : 'psh-main psh-main--padded'
          }
        >
          {children}
        </main>
      </div>
    </div>
  );
}

